import { useState, useEffect } from "react";
import { useTranslatedToast } from "@/hooks/useTranslatedToast";
import { useSearchParams } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { supabase } from "@/integrations/supabase/client";
import { useUser } from "@/hooks/auth";
import { useNotificationPreferences } from "@/hooks/useNotificationPreferences";
import { useActiveSessions } from "@/hooks/useActiveSessions";
import { useTranslation } from "@/hooks/useTranslation";
import { Bell, Shield, Monitor, Mail } from "lucide-react";
import { PageShell } from "@/components/ui/progressive-loading";
import { GenericPageHero } from "@/components/shared/GenericPageHero";
import { Skeleton } from "@/components/ui/skeleton";
import { formatDistanceToNow } from "date-fns";

export default function UserSettingsPage() {
  const { user } = useUser();
  const toast = useTranslatedToast();
  const { t } = useTranslation('pages');
  const { t: tUI } = useTranslation('ui');
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  
  // Get the tab from URL parameters, default to notifications
  const defaultTab = searchParams.get('tab') || 'notifications';

  // Notification Preferences Hook
  const {
    preferences: notificationPreferences,
    loading: notificationLoading,
    updatePreference,
    getCategoryLabel,
    getCategoryDescription
  } = useNotificationPreferences();

  // Active Sessions Hook
  const {
    sessions,
    loading: sessionsLoading,
    revokeSession,
    formatDeviceInfo,
    getLocationFromIP
  } = useActiveSessions();

  const [emailChangeState, setEmailChangeState] = useState({
    newEmail: '',
    currentOtp: '',
    newOtp: '',
    requestId: null as string | null,
    step: 'input' as 'input' | 'verify' | 'complete',
    expiresAt: null as string | null,
    attemptsRemaining: 5
  });


  const handleNotificationToggle = async (category: any, field: 'email_enabled' | 'in_app_enabled', enabled: boolean) => {
    await updatePreference(category, { [field]: enabled });
  };

  const initiateEmailChange = async () => {
    if (!emailChangeState.newEmail) {
      toast.raw.error('Please enter a new email address');
      return;
    }

    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('initiate-email-change', {
        body: { newEmail: emailChangeState.newEmail }
      });

      if (error) {
        toast.raw.error(error.message || 'Failed to initiate email change');
        return;
      }

      setEmailChangeState(prev => ({
        ...prev,
        step: 'verify',
        requestId: data.requestId,
        expiresAt: data.expiresAt
      }));
      
      toast.raw.success('Verification codes sent to both email addresses');
    } catch (error: any) {
      toast.raw.error(error.message || 'Failed to send verification codes');
    } finally {
      setLoading(false);
    }
  };

  const verifyEmailChange = async () => {
    if (!emailChangeState.currentOtp || !emailChangeState.newOtp) {
      toast.raw.error('Please enter both verification codes');
      return;
    }

    if (emailChangeState.currentOtp.length !== 6 || emailChangeState.newOtp.length !== 6) {
      toast.raw.error('Please enter valid 6-digit codes');
      return;
    }

    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('verify-email-change', {
        body: {
          currentEmailOTP: emailChangeState.currentOtp,
          newEmailOTP: emailChangeState.newOtp
        }
      });

      if (error) {
        if (data?.attemptsRemaining !== undefined) {
          toast.raw.error(`Invalid codes. ${data.attemptsRemaining} attempts remaining.`);
          setEmailChangeState(prev => ({ ...prev, attemptsRemaining: data.attemptsRemaining }));
        } else {
          toast.raw.error(error.message || 'Verification failed');
        }
        return;
      }

      toast.raw.success('Email address updated successfully!');
      
      // Reset form
      setEmailChangeState({
        newEmail: '',
        currentOtp: '',
        newOtp: '',
        requestId: null,
        step: 'input',
        expiresAt: null,
        attemptsRemaining: 5
      });
    } catch (error: any) {
      toast.raw.error(error.message || 'Failed to verify codes');
    } finally {
      setLoading(false);
    }
  };

  const resetEmailChange = () => {
    setEmailChangeState({
      newEmail: '',
      currentOtp: '',
      newOtp: '',
      requestId: null,
      step: 'input',
      expiresAt: null,
      attemptsRemaining: 5
    });
  };

  const handleRevokeSession = async (sessionId: string) => {
    if (!confirm('Are you sure you want to revoke this session?')) {
      return;
    }
    await revokeSession(sessionId);
  };

  return (
    <PageShell>
      <GenericPageHero
        icon={Shield}
        title={t('settings.title')}
        description={t('settings.description')}
      />

      <Tabs defaultValue={defaultTab} className="space-y-6">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="notifications">{t('pages:settings.notifications')}</TabsTrigger>
          <TabsTrigger value="security">{t('pages:settings.security')}</TabsTrigger>
        </TabsList>

        <TabsContent value="notifications" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-sm font-medium">
                <Bell className="w-4 h-4" />
                {t('pages:settings.notification_preferences')}
              </CardTitle>
              <CardDescription className="text-xs">{t('pages:settings.customize_notifications')}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {notificationLoading ? (
                <div className="space-y-4">
                  {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="flex items-center justify-between">
                      <div className="space-y-2 flex-1">
                        <Skeleton className="h-4 w-48" />
                        <Skeleton className="h-3 w-72" />
                      </div>
                      <Skeleton className="h-6 w-11 rounded-full" />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="space-y-4">
                  {notificationPreferences.map((prefs, idx) => (
                    <div key={prefs.category}>
                      <h4 className="font-medium mb-3">{getCategoryLabel(prefs.category)}</h4>
                      <div className="space-y-3 pl-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium text-sm">{tUI('notification_preferences.email_notifications')}</p>
                            <p className="text-xs text-muted-foreground">{getCategoryDescription(prefs.category)}</p>
                          </div>
                          <Switch
                            checked={prefs.email_enabled}
                            onCheckedChange={(checked) => handleNotificationToggle(prefs.category, 'email_enabled', checked)}
                          />
                        </div>
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium text-sm">{tUI('notification_preferences.inapp_notifications')}</p>
                            <p className="text-xs text-muted-foreground">{tUI('notification_preferences.inapp_notifications_description')}</p>
                          </div>
                          <Switch
                            checked={prefs.in_app_enabled}
                            onCheckedChange={(checked) => handleNotificationToggle(prefs.category, 'in_app_enabled', checked)}
                          />
                        </div>
                      </div>
                      {idx < notificationPreferences.length - 1 && (
                        <Separator className="mt-4" />
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="security" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-sm font-medium">
                <Mail className="w-4 h-4" />
                {tUI('settings.email_address')}
              </CardTitle>
              <CardDescription className="text-xs">{tUI('settings.security_description')}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="current-email">{tUI('settings.current_email')}</Label>
                  <Input 
                    id="current-email" 
                    type="email" 
                    value={user?.email || ''}
                    disabled
                    className="bg-muted"
                  />
                </div>
                
                {emailChangeState.step === 'input' && (
                  <>
                    <div className="space-y-2">
                      <Label htmlFor="new-email">{tUI('settings.new_email')}</Label>
                      <Input 
                        id="new-email" 
                        type="email" 
                        value={emailChangeState.newEmail}
                        onChange={(e) => setEmailChangeState({...emailChangeState, newEmail: e.target.value})}
                        placeholder={tUI('settings.enter_new_email')}
                        disabled={loading}
                      />
                    </div>

                    <Button 
                      variant="outline" 
                      onClick={initiateEmailChange} 
                      disabled={loading || !emailChangeState.newEmail}
                    >
                      {loading ? tUI('common.sending') : tUI('settings.update_email')}
                    </Button>
                  </>
                )}

                {emailChangeState.step === 'verify' && (
                  <>
                    <div className="p-4 bg-muted/50 rounded-lg space-y-2">
                      <p className="text-sm font-medium">{tUI('settings.dual_verification_required')}</p>
                      <p className="text-xs text-muted-foreground">
                        {tUI('settings.verification_codes_sent', { currentEmail: user?.email, newEmail: emailChangeState.newEmail })}
                      </p>
                      {emailChangeState.expiresAt && (
                        <p className="text-xs text-muted-foreground">
                          {tUI('settings.codes_expire_in')}
                        </p>
                      )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="current-otp">{tUI('settings.code_from_current_email')}</Label>
                        <p className="text-xs text-muted-foreground mb-2">
                          {tUI('settings.sent_to', { email: user?.email })}
                        </p>
                        <Input 
                          id="current-otp" 
                          type="text" 
                          maxLength={6}
                          value={emailChangeState.currentOtp}
                          onChange={(e) => setEmailChangeState({
                            ...emailChangeState, 
                            currentOtp: e.target.value.replace(/\D/g, '')
                          })}
                          placeholder="000000"
                          className="text-center text-lg tracking-widest"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="new-otp">{tUI('settings.code_from_new_email')}</Label>
                        <p className="text-xs text-muted-foreground mb-2">
                          {tUI('settings.sent_to', { email: emailChangeState.newEmail })}
                        </p>
                        <Input 
                          id="new-otp" 
                          type="text" 
                          maxLength={6}
                          value={emailChangeState.newOtp}
                          onChange={(e) => setEmailChangeState({
                            ...emailChangeState, 
                            newOtp: e.target.value.replace(/\D/g, '')
                          })}
                          placeholder="000000"
                          className="text-center text-lg tracking-widest"
                        />
                      </div>
                    </div>

                    {emailChangeState.attemptsRemaining < 5 && (
                      <p className="text-sm text-destructive">
                        {tUI('settings.attempts_remaining', { count: emailChangeState.attemptsRemaining })}
                      </p>
                    )}
                    
                    <div className="flex gap-2">
                      <Button 
                        variant="outline" 
                        onClick={verifyEmailChange} 
                        disabled={loading || emailChangeState.currentOtp.length !== 6 || emailChangeState.newOtp.length !== 6}
                      >
                        {loading ? tUI('settings.verifying') : tUI('settings.verify_update_email')}
                      </Button>
                      <Button 
                        variant="ghost" 
                        onClick={resetEmailChange}
                        disabled={loading}
                      >
                        {tUI('settings.cancel')}
                      </Button>
                    </div>
                  </>
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-sm font-medium">
                <Monitor className="w-4 h-4" />
                {tUI('settings.active_sessions')}
              </CardTitle>
              <CardDescription className="text-xs">{tUI('settings.manage_active_sessions')}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {sessionsLoading ? (
                <div className="space-y-3">
                  {[1, 2].map((i) => (
                    <div key={i} className="flex items-start justify-between p-3 border rounded-lg">
                      <div className="space-y-2 flex-1">
                        <Skeleton className="h-4 w-32" />
                        <Skeleton className="h-3 w-48" />
                        <Skeleton className="h-3 w-24" />
                      </div>
                      <Skeleton className="h-8 w-20" />
                    </div>
                  ))}
                </div>
              ) : sessions.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">
                  {tUI('settings.no_active_sessions')}
                </p>
              ) : (
                <div className="space-y-3">
                  {sessions.map((session) => (
                    <div 
                      key={session.id} 
                      className="flex items-start justify-between p-3 border rounded-lg"
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <Monitor className="w-4 h-4 text-muted-foreground" />
                          <p className="font-medium text-sm">
                            {formatDeviceInfo(session)}
                            {session.is_current && (
                              <span className="ml-2 text-xs bg-primary/10 text-primary px-2 py-0.5 rounded">
                                {tUI('settings.current_session')}
                              </span>
                            )}
                          </p>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {getLocationFromIP(session.ip_address)}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {tUI('settings.last_active', { time: formatDistanceToNow(new Date(session.last_accessed_at), { addSuffix: true }) })}
                        </p>
                      </div>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => handleRevokeSession(session.id)}
                        disabled={session.is_current}
                      >
                        {session.is_current ? tUI('settings.active') : tUI('settings.revoke')}
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </PageShell>
  );
}
